import React, { useMemo } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useGetList, useListContext, useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import { ServiceInspectionMapView } from '@/components/service/map/ServiceInspectionMapView.jsx';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';

const MAP_ID = 'ef998239259c4672dac8d1bc';

const StyledMapBox = styled(Box)(({ theme }) => ({
    '&.service-inspection-map': {
        '& .bulk-action-buttons': {
            position: 'fixed',
            display: 'flex',
            gap: '16px',
            bottom: '0px',
            right: 'auto',
            zIndex: 1000,
            marginLeft: '-40px',
            width: '-webkit-fill-available',
            padding: '32px 40px',
            backgroundColor: Palette.white,
            boxShadow: Palette.boxShadow.elevation12,
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            '& .MuiButton-textPrimary': {
                backgroundColor: Palette.gray.gray100,
                color: `${Palette.black} !important`,
                '&:hover': {
                    boxShadow: `inset 0 0 0 2px ${Palette.gray.gray300}`,
                },
            },
        },
        '& .pos-marker-container': {
            '& .pos-marker-label, & .pos-marker-checkbox': {
                [theme.breakpoints.down('lg')]: {
                    display: 'none',
                },
            },
        },
    },
}));

export const ServiceInspectionMap = ({ listFilter, bulkActionButtons }) => {
    const translate = useTranslate();
    const { filterValues, sort, selectedIds } = useListContext();
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const combinedFilter = useMemo(() => ({ ...listFilter, ...filterValues }), [listFilter, filterValues]);

    const {
        data: inspections,
        isLoading: isLoadingInspections,
        error: inspectionsError,
    } = useGetList('service-inspection', {
        pagination: { page: 1, perPage: 1000 },
        sort,
        filter: combinedFilter,
    });

    const collectionPointData = useMemo(() => {
        const collectionPointMap = new Map();
        const hasCoordinates = collectionPoint => {
            const lat = collectionPoint?.geoPoint?.latitude ?? collectionPoint?.geoPoint?.lat ?? collectionPoint?.latitude;
            const lng = collectionPoint?.geoPoint?.longitude ?? collectionPoint?.geoPoint?.lng ?? collectionPoint?.longitude;
            return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
        };

        (inspections || []).forEach(record => {
            const collectionPoint = record?.collectionPoint ?? record?.device?.collectionPoint;
            const collectionPointId =
                collectionPoint?.id ?? collectionPoint?.collectionPointId ?? record?.collectionPointId ?? record?.device?.collectionPointId;
            if (!collectionPoint || !collectionPointId) {
                return;
            }
            const key = String(collectionPointId);
            const dueDate = record?.dueDate;
            const scheduledDate = record?.scheduledDate;
            const serialNumber = record?.device?.serialNumber;
            const deviceState = record?.device?.state;
            const deviceId = record?.device?.id;
            const existing = collectionPointMap.get(key);
            const shouldReplace = !existing || (!existing.__hasCoords && hasCoordinates(collectionPoint));

            if (shouldReplace) {
                collectionPointMap.set(key, {
                    ...collectionPoint,
                    dueDate,
                    scheduledDate,
                    serialNumber,
                    deviceState,
                    deviceId,
                    inspectionId: record?.id,
                    __hasCoords: hasCoordinates(collectionPoint),
                });
            }
        });

        const result = Array.from(collectionPointMap.values());
        result.forEach(collectionPoint => {
            delete collectionPoint.__hasCoords;
        });
        return result;
    }, [inspections]);

    if (!googleMapsApiKey) {
        return (
            <Box p={2}>
                <Alert severity='info'>{translate('common.map.noMapKey')}</Alert>
            </Box>
        );
    }

    if (isLoadingInspections) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                <CircularProgress />
            </Box>
        );
    }

    if (inspectionsError) {
        return (
            <Box p={2}>
                <Alert severity='error'>{translate('resources.serviceInspection.labels.mapLoadError', { message: inspectionsError.message })}</Alert>
            </Box>
        );
    }

    if (!collectionPointData.length) {
        return (
            <Box p={2}>
                <Alert severity='info'>{translate('common.map.noLocationData')}</Alert>
            </Box>
        );
    }

    return (
        <StyledMapBox className='service-inspection-map'>
            {bulkActionButtons && selectedIds?.length ? <Box className='bulk-action-buttons'>{bulkActionButtons}</Box> : null}
            <ServiceInspectionMapView collectionPointData={collectionPointData} googleMapsApiKey={googleMapsApiKey} mapId={MAP_ID} />
        </StyledMapBox>
    );
};

ServiceInspectionMap.propTypes = {
    listFilter: PropTypes.object,
    bulkActionButtons: PropTypes.node,
};
